/**
 * The cash day book (owner, 21 Oct 2026: "create a day book for surbhi for
 * adding company expense in cash heads will be 1. factory expense 2. office
 * expense 3. renovation and construction 4. unloading expense 5. freight
 * expense 6. salary expense").
 *
 * Every rupee paid out in cash is one entry: the day, its head, how much, and
 * who it was paid to. It is kept per book — the company whose cash it is —
 * and kept by whoever keeps the company's expenses (`reconcilesExpenses`:
 * Surbhi, and the owner's desks). An entry made in error is cancelled with a
 * reason, never deleted.
 */
import { reconcilesExpenses } from './expense-recon.js';

/**
 * The heads the day book started with (owner, 21 Oct 2026). Since 22 Oct 2026
 * the keepers add heads and rename them ("give a option in daybook for adding
 * and editing heads of expense"): the list lives in the CashHead table, one list
 * for every book. A head's key never changes — its entries follow a rename —
 * and a head is put away, never deleted, so old entries keep theirs. These six
 * are written back only if the list is ever found empty.
 */
export const DEFAULT_CASH_HEADS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'FACTORY', label: 'Factory expense' },
  { key: 'OFFICE', label: 'Office expense' },
  { key: 'RENOVATION', label: 'Renovation and construction' },
  { key: 'UNLOADING', label: 'Unloading expense' },
  { key: 'FREIGHT', label: 'Freight expense' },
  { key: 'SALARY', label: 'Salary expense' },
];

/** A head's key: FACTORY … for the first six, one made from its first name after. */
export type CashHeadKey = string;

/** The longest a head's name may be. */
export const CASH_HEAD_LABEL_MAX = 60;

/** A head as the keepers manage it. */
export interface CashHeadView {
  key: CashHeadKey;
  label: string;
  sortOrder: number;
  /** Put away: no new entry under it; its old entries keep it. */
  isActive: boolean;
  /** Live entries under it in this book. */
  entries: number;
}

/** Who keeps the day book: the company's expense keeper — the owner's desks, and whoever is switched on for expenses in Users. */
export function keepsDayBook(user: { email: string; organizationId?: string; reconcilesExpenses?: boolean | null } | null | undefined): boolean {
  return reconcilesExpenses(user);
}

/** The most one entry may be: a crore. */
export const DAY_BOOK_MAX_AMOUNT = 10_000_000;
/** How far back an entry may be dated: two years, for vouchers found late. */
export const DAY_BOOK_BACK_DAYS = 731;

export interface DayBookEntry {
  id: string;
  /** Paid out, or received into the cash box (22 Oct 2026). */
  direction: CashDirectionKey;
  /** YYYY-MM-DD, the day the cash was paid or received (IST). */
  day: string;
  /** A payment's head; none on cash received. */
  head: CashHeadKey | null;
  /** The head's name as it reads now; null on cash received. */
  headLabel: string | null;
  amount: number;
  /** Who the cash was paid to — or, on cash received, who it came from. */
  paidTo: string;
  billNo: string | null;
  note: string | null;
  hasPhoto: boolean;
  by: string | null;
  createdAt: string;
  updatedAt: string;
  /** Cancelled: kept, out of every total. */
  voidedAt: string | null;
  voidedBy: string | null;
  voidReason: string | null;
}

export interface DayBookTotals {
  /** Paid out, and how many payments. */
  total: number;
  count: number;
  /** Received into the cash box, and how many receipts. */
  received: number;
  receivedCount: number;
  /** Every head in use, in the keepers' order, nought where nothing was paid — a put-away head only where it has entries. */
  byHead: Array<{ head: CashHeadKey; label: string; amount: number; count: number }>;
  /**
   * Day by day, newest first: paid (amount) and received, and — from the day the
   * cash in hand was counted — the cash the day opened and closed with. The
   * balance is the whole cash box, whatever head or search is picked.
   */
  byDay: Array<{ day: string; amount: number; count: number; received: number; opening: number | null; closing: number | null }>;
}

/** The cash box over the days picked (owner, 22 Oct 2026: carried forward). */
export interface DayBookBalance {
  /** The cash in hand it counts from, or null until the keeper sets it. */
  start: { day: string; amount: number; by: string | null } | null;
  /** The days the figures below span: from the later of the period's start and the counted day, to the earlier of its end and today. */
  from: string | null;
  to: string | null;
  opening: number | null;
  received: number;
  paid: number;
  closing: number | null;
}

/** Paid out under a head, or received into the cash box (22 Oct 2026). */
export const CASH_DIRECTIONS = ['OUT', 'IN'] as const;
export type CashDirectionKey = (typeof CASH_DIRECTIONS)[number];

export interface DayBookInput {
  /** Paid out (the default), or received. */
  direction?: CashDirectionKey;
  day: string;
  /** A payment's head; none on cash received. */
  head?: CashHeadKey | null;
  amount: number;
  paidTo: string;
  billNo?: string | null;
  note?: string | null;
}
